<template>
  <div ref="componentRef" class="application-component note-component" data-selection-owner="note">
    <div class="application-component-title">{{ componentTitle }}</div>
    <div class="application-component-content">
      <div v-if="readOnly" class="placeholder">
        <el-tooltip effect="dark" placement="bottom" :visible="true" content="AI对话引用    AI释义    插入批注">
          <el-skeleton :rows="1" animated />
        </el-tooltip>
        <el-skeleton :rows="1" animated />
        <el-tooltip effect="dark" placement="bottom" :visible="true" content="批注：示例批注内容">
          <el-divider class="placeholder-divider" />
        </el-tooltip>
      </div>
      <div
        v-else
        class="paragraph note-paragraph"
        :style="{ fontSize }"
        v-loading="isLoading"
        @mouseup="handleTextSelection"
      >
        <div
          v-for="item in renderedParagraphs"
          :id="item.anchorId"
          :key="item.anchorId"
          class="paragraph-item textselection"
          :data-anchor-id="item.anchorId"
          :data-paragraph-index="item.index"
        >
          <p>
            <template v-for="segment in item.segments" :key="segment.key">
              <span
                v-if="!segment.annotation"
                :class="{ 'feature-highlight': segment.featureActive }"
              >
                {{ segment.text }}
              </span>
              <el-tooltip v-else :content="segment.annotation.ann" placement="top" effect="dark">
                <span :class="['annotated-text', { 'feature-highlight annotated-feature-highlight': segment.featureActive }]">
                  {{ segment.text }}
                </span>
              </el-tooltip>
            </template>
          </p>
        </div>
        <el-empty v-if="!renderedParagraphs.length && !isLoading" description="暂无可展示内容" />
      </div>
    </div>

    <div v-if="showToolbar" class="note-selection-toolbar" :style="toolbarStyle">
      <div @click="handleAIChat">AI对话引用</div>
      <div @click="handleAIExplain">AI释义</div>
      <div @click="handleAddAnnotation">插入批注</div>
    </div>

    <div v-if="!readOnly" :class="['application-component-footer', { open: openFooter }]" @mouseleave="openFooter = false">
      <template v-if="openFooter">
        <div class="application-component-footer-item" @click="handleExport">添加交流清单</div>
      </template>
      <div class="application-component-footer-trigger" @mouseenter="openFooter = true">
        <svg-icon name="arrow-bottom.png" :size="130" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { ElLoading, ElMessage } from 'element-plus'
import { useGlobalStore } from '@/store/modules/global'
import { useWritingStore } from '@/store/modules/writing'
import { fetchSaveComponent } from '@/api/writing'
import { buildAnnotationDetailListFileItem } from '@/components/Application/detailList'
import { jsonParse } from '@/utils'

const globalStore = useGlobalStore()
const writingStore = useWritingStore()

const props = defineProps({
  readOnly: {
    type: Boolean,
    default: false
  },
  data: {
    type: Object,
    default: () => null,
  },
  application: {
    type: Object,
    default: () => null
  },
  projectId: {
    type: String,
    default: ''
  },
  handleAddToDetailList: {
    type: Function,
    default: () => null
  },
  handleUpdateDetailList: {
    type: Function,
    default: () => null
  }
})

const componentTypeCode = 'comp_ann'
const FEATURE_BREAKDOWN_EVENT = 'feature-breakdown-focus'
const WORKSPACE_FILE_FOCUS_EVENT = 'workspace-file-focus'
const componentRef = ref(null)
const openFooter = ref(false)
const annotationList = ref([])
const activeFeatureRanges = ref([])
const showToolbar = ref(false)
const toolbarStyle = ref({
  top: '0px',
  left: '0px'
})
const selectedText = ref('')
const selectedSelection = ref(null)
const componentTitle = computed(() => props.application?.name || '查阅批注')

const CONTROL_CHAR_REGEX = /[\u0000-\u0008\u000B-\u001F\u007F]/g
const PAGE_FIELD_REGEX = /^PAGE\s+\d+$/i

const sanitizeParagraphText = (text = '') => {
  const normalized = text
    .replace(/\r\n?/g, '\n')
    .replace(CONTROL_CHAR_REGEX, '')
    .replace(/\u00A0/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim()

  if (PAGE_FIELD_REGEX.test(normalized.replace(/\s+/g, ' '))) {
    return ''
  }

  return normalized
}

const normalizeAnnotationList = (value) => {
  if (!Array.isArray(value)) {
    return []
  }

  return value
    .map((item, index) => {
      const paragraphIndex = Number(item?.paragraphIndex ?? item?.startSelection?.startIndex)
      const startOffset = Number(item?.startOffset)
      const endOffset = Number(item?.endOffset)

      return {
        ...item,
        id: item?.id || `note-ann-${index}`,
        paragraphIndex: Number.isFinite(paragraphIndex) ? paragraphIndex : null,
        startOffset: Number.isFinite(startOffset) ? startOffset : null,
        endOffset: Number.isFinite(endOffset) ? endOffset : null,
      }
    })
    .filter(item => item?.ann && item?.startSelection?.text)
}

const paragraphList = computed(() => {
  const contentItems = jsonParse(props.data?.docResult || '[]')

  return contentItems
    .filter(item => item?.type === 'TEXT')
    .map((item, index) => {
      const paragraphIndex = item?.index ?? index
      const text = sanitizeParagraphText(item?.text || '')

      return {
        anchorId: `note-paragraph-${paragraphIndex}`,
        index: paragraphIndex,
        text,
      }
    })
    .filter(item => item.text)
})

const normalizeFeatureRanges = (value) => {
  if (!Array.isArray(value)) {
    return []
  }

  return value
    .map((item, index) => {
      const paragraphIndex = Number(item?.paragraphIndex)
      const startOffset = Number(item?.startOffset)
      const endOffset = Number(item?.endOffset)
      const normalizedParagraphIndex = Number.isFinite(paragraphIndex) ? paragraphIndex : null

      return {
        ...item,
        id: item?.id || `feature-range-${index}`,
        anchorId: item?.anchorId || (Number.isInteger(normalizedParagraphIndex) ? `note-paragraph-${normalizedParagraphIndex}` : ''),
        paragraphIndex: normalizedParagraphIndex,
        startOffset: Number.isFinite(startOffset) ? startOffset : null,
        endOffset: Number.isFinite(endOffset) ? endOffset : null,
      }
    })
    .filter(item =>
      Number.isInteger(item.paragraphIndex) &&
      Number.isInteger(item.startOffset) &&
      Number.isInteger(item.endOffset) &&
      item.endOffset > item.startOffset
    )
}

const buildParagraphSegments = (text, annotations, featureRanges) => {
  if (!text) {
    return []
  }

  const boundaries = new Set([0, text.length])

  annotations.forEach((annotation) => {
    boundaries.add(Math.max(0, Math.min(text.length, annotation.startOffset)))
    boundaries.add(Math.max(0, Math.min(text.length, annotation.endOffset)))
  })

  featureRanges.forEach((range) => {
    boundaries.add(Math.max(0, Math.min(text.length, range.startOffset)))
    boundaries.add(Math.max(0, Math.min(text.length, range.endOffset)))
  })

  const orderedBoundaries = Array.from(boundaries).sort((left, right) => left - right)
  const segments = []

  for (let index = 0; index < orderedBoundaries.length - 1; index += 1) {
    const start = orderedBoundaries[index]
    const end = orderedBoundaries[index + 1]

    if (end <= start) {
      continue
    }

    const segmentText = text.slice(start, end)
    if (!segmentText) {
      continue
    }

    const annotation = annotations.find(item => start >= item.startOffset && end <= item.endOffset) || null
    const featureActive = featureRanges.some(item => start >= item.startOffset && end <= item.endOffset)

    segments.push({
      key: `segment-${start}-${end}-${annotation?.id || 'plain'}-${featureActive ? 'feature' : 'normal'}`,
      text: segmentText,
      annotation,
      featureActive,
    })
  }

  return segments.length ? segments : [{ key: `${text.length}-plain-fallback`, text, annotation: null, featureActive: false }]
}

const renderedParagraphs = computed(() => {
  return paragraphList.value.map(item => {
    const paragraphAnnotations = annotationList.value
      .filter(annotation =>
        annotation.paragraphIndex === item.index &&
        Number.isInteger(annotation.startOffset) &&
        Number.isInteger(annotation.endOffset) &&
        annotation.startOffset >= 0 &&
        annotation.endOffset > annotation.startOffset
      )
      .sort((left, right) => left.startOffset - right.startOffset)
      .filter((annotation, index, list) => {
        if (index === 0) {
          return true
        }
        return annotation.startOffset >= list[index - 1].endOffset
      })
    const paragraphFeatureRanges = activeFeatureRanges.value
      .filter(range =>
        range.paragraphIndex === item.index &&
        Number.isInteger(range.startOffset) &&
        Number.isInteger(range.endOffset) &&
        range.startOffset >= 0 &&
        range.endOffset > range.startOffset
      )
      .sort((left, right) => left.startOffset - right.startOffset)

    return {
      ...item,
      segments: buildParagraphSegments(item.text, paragraphAnnotations, paragraphFeatureRanges),
    }
  })
})

const isLoading = computed(() => !props.data && !props.readOnly)
const fontSize = computed(() => `${globalStore.fontSize}px`)

watch(
  () => props.data?.extOne,
  (newValue) => {
    annotationList.value = normalizeAnnotationList(jsonParse(newValue || '[]'))
  },
  { immediate: true }
)

const resolveFeatureAnchorElement = (range = {}) => {
  if (range?.anchorId) {
    const anchorElement = document.getElementById(range.anchorId)
    if (anchorElement) {
      return anchorElement
    }
  }

  if (!Number.isInteger(range?.paragraphIndex)) {
    return null
  }

  return componentRef.value?.querySelector?.(`.paragraph-item[data-paragraph-index="${range.paragraphIndex}"]`) || null
}

const scrollToFeatureRange = async (sourceRanges = []) => {
  const firstRange = sourceRanges[0]
  await nextTick()

  const anchorElement = resolveFeatureAnchorElement(firstRange)
  if (!anchorElement) {
    return
  }

  anchorElement.scrollIntoView({
    behavior: 'smooth',
    block: 'center',
  })
}

const matchFocusTarget = (targetItemId = '') => {
  if (!targetItemId) {
    return false
  }
  return targetItemId === props.application?.id
}

const handleFeatureBreakdownFocus = (event) => {
  const { projectId, sourceRanges, itemId } = event.detail || {}
  if (projectId !== props.projectId) {
    return
  }
  if (itemId) {
    if (!matchFocusTarget(itemId)) {
      return
    }
  } else if (props.application?.id && props.application.id !== 'Note') {
    return
  }

  const normalizedRanges = normalizeFeatureRanges(sourceRanges)
  activeFeatureRanges.value = normalizedRanges
  scrollToFeatureRange(normalizedRanges)
}

const clearSelection = () => {
  showToolbar.value = false
  selectedText.value = ''
  selectedSelection.value = null
  window.getSelection()?.removeAllRanges()
}

const getParagraphElement = (node) => {
  if (!node) {
    return null
  }

  const element = node.nodeType === Node.TEXT_NODE ? node.parentElement : node
  return element?.closest?.('.paragraph-item') || null
}

const getTextOffset = (root, container, offset) => {
  const range = document.createRange()
  range.selectNodeContents(root)
  range.setEnd(container, offset)
  return range.toString().length
}

const getToolbarPosition = (rect) => {
  const containerRect = componentRef.value?.getBoundingClientRect()
  const toolbarWidth = 240
  const toolbarHeight = 36

  let left = rect.left + rect.width / 2
  let top = rect.bottom + 8

  if (containerRect) {
    const halfWidth = toolbarWidth / 2
    const minLeft = containerRect.left + halfWidth + 8
    const maxLeft = containerRect.right - halfWidth - 8
    left = Math.max(minLeft, Math.min(maxLeft, left))

    const maxTop = containerRect.bottom - toolbarHeight - 8
    if (top > maxTop) {
      top = Math.max(containerRect.top + 8, rect.top - toolbarHeight - 8)
    }
  }

  return {
    left: `${left}px`,
    top: `${top}px`,
  }
}

const handleTextSelection = () => {
  const selection = window.getSelection()

  if (!selection || selection.isCollapsed || !selection.toString().trim()) {
    clearSelection()
    return
  }

  const range = selection.getRangeAt(0)
  const startParagraph = getParagraphElement(range.startContainer)
  const endParagraph = getParagraphElement(range.endContainer)

  if (!startParagraph || !endParagraph || startParagraph !== endParagraph) {
    showToolbar.value = true
    selectedText.value = selection.toString()
    selectedSelection.value = {
      multiParagraph: true,
    }
    toolbarStyle.value = getToolbarPosition(range.getBoundingClientRect())
    return
  }

  const textRoot = startParagraph.querySelector('p')
  const startOffset = getTextOffset(textRoot, range.startContainer, range.startOffset)
  const endOffset = getTextOffset(textRoot, range.endContainer, range.endOffset)
  const paragraphIndex = Number(startParagraph.dataset.paragraphIndex)
  const exactText = textRoot.textContent.slice(startOffset, endOffset)

  if (!exactText.trim()) {
    clearSelection()
    return
  }

  selectedText.value = exactText
  selectedSelection.value = {
    multiParagraph: false,
    anchorId: startParagraph.dataset.anchorId,
    paragraphIndex,
    startOffset,
    endOffset,
    text: exactText,
  }
  toolbarStyle.value = getToolbarPosition(range.getBoundingClientRect())
  showToolbar.value = true
}

const handleAIChat = () => {
  if (!selectedText.value) {
    return
  }

  const quoteText = selectedText.value
  globalStore.setAIVisible(true)
  setTimeout(() => {
    globalStore.setQuoteAIChat({
      title: componentTitle.value,
      text: quoteText,
    })
  }, 200)
  clearSelection()
}

const handleAIExplain = () => {
  if (!selectedText.value) {
    return
  }

  const quoteText = selectedText.value
  globalStore.setAIVisible(true)
  setTimeout(() => {
    globalStore.setSendAIChat(quoteText)
  }, 200)
  clearSelection()
}

const syncAnnotationState = (extOne) => {
  const nextStep2Info = {
    ...(writingStore.step2Info || {}),
    step2Component: {
      ...(writingStore.step2Info?.step2Component || {}),
      [componentTypeCode]: {
        ...(writingStore.step2Info?.step2Component?.[componentTypeCode] || {}),
        ...(props.data || {}),
        extOne,
      }
    }
  }

  writingStore.setStep2Info(nextStep2Info)
}

const saveAnnotations = async (nextAnnotations) => {
  const extOne = JSON.stringify(nextAnnotations)
  const payload = {
    disclosureProjectId: props.projectId,
    componentTypeCode,
    docResult: props.data?.docResult || '',
    extOne,
  }

  const loading = ElLoading.service({
    lock: true,
    text: '保存批注中...',
    background: 'rgba(0, 0, 0, 0.7)',
  })

  try {
    console.log('save note annotation payload', payload)
    const saveResult = await fetchSaveComponent(payload)
    if (!saveResult) {
      throw new Error('save_component_failed')
    }
    annotationList.value = nextAnnotations
    syncAnnotationState(extOne)
  } finally {
    loading.close()
  }
}

const handleAddAnnotation = async () => {
  if (!selectedSelection.value) {
    return
  }

  if (selectedSelection.value.multiParagraph) {
    ElMessage.warning('当前仅支持单段落内划词批注')
    clearSelection()
    return
  }

  const hasOverlap = annotationList.value.some(annotation =>
    annotation.paragraphIndex === selectedSelection.value.paragraphIndex &&
    Number.isInteger(annotation.startOffset) &&
    Number.isInteger(annotation.endOffset) &&
    Math.max(annotation.startOffset, selectedSelection.value.startOffset) < Math.min(annotation.endOffset, selectedSelection.value.endOffset)
  )

  if (hasOverlap) {
    ElMessage.warning('当前选区与已有批注重叠，请重新选择')
    clearSelection()
    return
  }

  const value = window.prompt('请输入批注内容', '')
  if (value === null) {
    clearSelection()
    return
  }

  const annotationText = value.trim()
  if (!annotationText) {
    ElMessage.warning('请输入批注内容')
    clearSelection()
    return
  }

  try {
    const nextAnnotations = [
      ...annotationList.value,
      {
        id: `note-ann-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        ann: annotationText,
        text: selectedSelection.value.text,
        createTime: Date.now(),
        paragraphIndex: selectedSelection.value.paragraphIndex,
        anchorId: selectedSelection.value.anchorId,
        startOffset: selectedSelection.value.startOffset,
        endOffset: selectedSelection.value.endOffset,
        startSelection: {
          startIndex: selectedSelection.value.paragraphIndex,
          text: selectedSelection.value.text,
        },
        endSelection: {
          startIndex: selectedSelection.value.paragraphIndex,
          text: selectedSelection.value.text,
        }
      }
    ]

    await saveAnnotations(nextAnnotations)
    ElMessage.success('批注已保存')
  } catch (error) {
    console.error('note annotation save failed', error)
    ElMessage.error('批注保存失败')
  } finally {
    clearSelection()
  }
}

const handleExport = () => {
  props.handleAddToDetailList(buildAnnotationDetailListFileItem(props.application))
  ElMessage.success('已加入交流清单')
}

const handleDocumentMouseDown = (event) => {
  if (componentRef.value?.contains(event.target)) {
    return
  }
  clearSelection()
}

onMounted(() => {
  document.addEventListener('mousedown', handleDocumentMouseDown)
  window.addEventListener(FEATURE_BREAKDOWN_EVENT, handleFeatureBreakdownFocus)
  window.addEventListener(WORKSPACE_FILE_FOCUS_EVENT, handleFeatureBreakdownFocus)
})

onUnmounted(() => {
  document.removeEventListener('mousedown', handleDocumentMouseDown)
  window.removeEventListener(FEATURE_BREAKDOWN_EVENT, handleFeatureBreakdownFocus)
  window.removeEventListener(WORKSPACE_FILE_FOCUS_EVENT, handleFeatureBreakdownFocus)
})
</script>

<style scoped lang="scss">
@import './common.scss';

.note-paragraph {
  height: 100%;
  overflow: auto;
  padding: 4px 0 24px;
}

.paragraph-item {
  color: #4B5563;
  line-height: 1.7;

  &:not(:last-child) {
    margin-bottom: 12px;
  }

  p {
    margin: 0;
    padding: 0;
    white-space: pre-wrap;
    word-break: break-word;
  }
}

.note-selection-toolbar {
  position: fixed;
  z-index: 1000;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 6px 16px;
  color: #fff;
  font-size: 12px;
  background: rgba(0, 0, 0, 0.82);
  border-radius: 6px;

  > div {
    cursor: pointer;
    white-space: nowrap;
  }
}

.annotated-text {
  color: #0f766e;
  background: rgba(15, 118, 110, 0.12);
  border-bottom: 1px solid rgba(15, 118, 110, 0.45);
  cursor: pointer;
}

.feature-highlight {
  background: rgba(250, 204, 21, 0.35);
  box-shadow: inset 0 -1px 0 rgba(202, 138, 4, 0.35);
}

.annotated-feature-highlight {
  background: linear-gradient(180deg, rgba(250, 204, 21, 0.32) 0%, rgba(15, 118, 110, 0.16) 100%);
}

.placeholder {
  :deep(.el-skeleton) {
    &:not(:last-child) {
      margin-bottom: 20px;
    }

    .el-skeleton__paragraph {
      margin-top: 8px;
    }

    .el-skeleton__item {
      display: block;
      background: #D3D7DC;
      border-radius: 0;

      &.is-first {
        width: 100%;
        background: #B8BBBF;
      }

      &:last-child {
        width: 80%;
      }
    }
  }

  .placeholder-divider {
    width: 80%;
    margin: 16px 0;
    border-top: 2px solid #111827;
  }
}

:deep(.el-loading-parent--relative) {
  width: 100%;
  height: 100%;
}
</style>
